/**
 * RelatedContentPicker (iter100.172)
 * ----------------------------------
 * A small multi-select for the doctor's OWN published articles + case
 * studies. Renders inside the article + case-study editors so the doctor
 * can pick up to 3 internal links that appear at the bottom of the
 * public page as a "Related from Dr. X" strip.
 *
 * Why this exists: internal links are a real SEO win for medical
 * content (E-E-A-T + topical authority). A picker is far more robust
 * than a raw URL input — no typos, no broken links, and it auto-hides
 * items that get unpublished / deleted.
 *
 * Props:
 *   • value: string[]      current related_ids
 *   • onChange: fn(next[]) new list
 *   • excludeId: string    the current item's id (won't appear in options)
 *   • api: axios instance  used to fetch the picker list
 *
 * Renders nothing when the doctor has no OTHER published pieces to link
 * to (fresh account) — with a small hint suggesting they publish their
 * first article/case study first.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Article, ImageSquare, Check, LinkSimple } from "@phosphor-icons/react";

const MAX_RELATED = 3;

const KIND_META = {
    article:    { label: "Article",    Icon: Article,     dot: "bg-sky-500" },
    case_study: { label: "Case Study", Icon: ImageSquare, dot: "bg-violet-500" },
};

export default function RelatedContentPicker({ value, onChange, excludeId, api }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState("");

    useEffect(() => {
        let cancelled = false;
        async function fetchList() {
            setLoading(true);
            try {
                const params = excludeId ? { exclude_id: excludeId } : {};
                const { data } = await api.get("/doctor/content/publishable-list", { params });
                if (!cancelled) setItems(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchList();
        return () => { cancelled = true; };
    }, [excludeId, api]);

    const selectedIds = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
    const atLimit = selectedIds.size >= MAX_RELATED;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => (it.title || "").toLowerCase().includes(q));
    }, [items, query]);

    const toggle = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else {
            if (atLimit) return;   // silent no-op at the cap
            next.add(id);
        }
        // Preserve insertion order for public rendering
        const asList = [];
        for (const rid of value || []) if (next.has(rid)) asList.push(rid);
        for (const rid of next) if (!asList.includes(rid)) asList.push(rid);
        onChange(asList);
    };

    // Reorder helpers so the doctor can control the "Related from…" order.
    const move = (id, dir) => {
        const arr = [...(value || [])];
        const idx = arr.indexOf(id);
        if (idx < 0) return;
        const swap = idx + dir;
        if (swap < 0 || swap >= arr.length) return;
        [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
        onChange(arr);
    };

    if (loading) {
        return (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-4 text-[12px] text-slate-500" data-testid="related-picker-loading">
                Loading your published pieces…
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-4 text-[12px] text-slate-500 leading-relaxed" data-testid="related-picker-empty">
                <div className="font-semibold text-slate-700 mb-0.5 flex items-center gap-1.5">
                    <LinkSimple size={14} weight="bold" /> No other published pieces yet
                </div>
                Publish another article or case study, and you&apos;ll be able to
                cross-link them here — a small but proven SEO lift on medical content.
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid="related-picker">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-slate-500">
                    {selectedIds.size} of {MAX_RELATED} picked
                </span>
                {atLimit && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Max reached
                    </span>
                )}
                <div className="ml-auto">
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter by title…"
                        className="h-8 px-2.5 text-[12px] rounded-md border border-slate-200 focus:border-slate-400 focus:outline-none w-44"
                        data-testid="related-picker-search"
                    />
                </div>
            </div>

            {/* Selected list on top (with order controls) */}
            {selectedIds.size > 0 && (
                <ol className="space-y-1.5" data-testid="related-picker-selected-list">
                    {(value || []).map((rid, idx) => {
                        const it = items.find((x) => x.id === rid);
                        if (!it) return null;
                        const meta = KIND_META[it.kind] || KIND_META.article;
                        return (
                            <li
                                key={rid}
                                className="flex items-center gap-2 bg-white rounded-md border border-slate-200 pl-2 pr-1 py-1.5"
                                data-testid={`related-picker-selected-${rid}`}
                            >
                                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[5rem]">
                                    {idx + 1}. {meta.label}
                                </span>
                                <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">
                                    {it.title || "(untitled)"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => move(rid, -1)}
                                    disabled={idx === 0}
                                    className="h-6 w-6 grid place-items-center text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    title="Move up"
                                    data-testid={`related-picker-up-${rid}`}
                                >↑</button>
                                <button
                                    type="button"
                                    onClick={() => move(rid, 1)}
                                    disabled={idx === (value || []).length - 1}
                                    className="h-6 w-6 grid place-items-center text-slate-400 hover:text-slate-700 disabled:opacity-30"
                                    title="Move down"
                                    data-testid={`related-picker-down-${rid}`}
                                >↓</button>
                                <button
                                    type="button"
                                    onClick={() => toggle(rid)}
                                    className="h-6 px-2 rounded text-[11px] font-semibold text-red-600 hover:bg-red-50"
                                    data-testid={`related-picker-remove-${rid}`}
                                >Remove</button>
                            </li>
                        );
                    })}
                </ol>
            )}

            {/* Candidate list */}
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                {filtered.length === 0 && (
                    <div className="px-3 py-6 text-center text-[12px] text-slate-500">
                        Nothing matches &ldquo;{query}&rdquo;.
                    </div>
                )}
                {filtered.map((it) => {
                    const selected = selectedIds.has(it.id);
                    const meta = KIND_META[it.kind] || KIND_META.article;
                    const disabled = !selected && atLimit;
                    return (
                        <button
                            key={it.id}
                            type="button"
                            onClick={() => toggle(it.id)}
                            disabled={disabled}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                selected
                                    ? "bg-emerald-50/60"
                                    : disabled
                                      ? "opacity-40 cursor-not-allowed"
                                      : "hover:bg-slate-50 cursor-pointer"
                            }`}
                            data-testid={`related-picker-option-${it.id}`}
                        >
                            <span className={`h-5 w-5 grid place-items-center rounded border ${
                                selected ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300"
                            }`}>
                                {selected && <Check size={12} weight="bold" />}
                            </span>
                            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} shrink-0`} aria-hidden="true" />
                            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[4.5rem] shrink-0">
                                {meta.label}
                            </span>
                            <span className="text-[13px] text-slate-800 truncate flex-1">
                                {it.title || "(untitled)"}
                            </span>
                        </button>
                    );
                })}
            </div>

            <p className="text-[11px] text-slate-500 leading-snug">
                Small tip — pick pieces on the same topic (e.g. a case study of a crowding
                treatment + your article on Invisalign vs. braces). It signals topical
                depth to Google and keeps patients reading your work longer.
            </p>
        </div>
    );
}
